import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

interface ArticlePreviewProps {
  url: string;
}

const ArticlePreview: React.FC<ArticlePreviewProps> = ({ url }) => {
  const [metadata, setMetadata] = useState<{
    title?: string;
    description?: string;
    image?: string;
    siteName?: string;
  } | null>(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        // In a real implementation, you would want to fetch metadata from your backend
        // to avoid CORS issues and to safely scrape the target website
        // For now, we'll show a simplified preview
        const domain = new URL(url).hostname.replace('www.', '');
        setMetadata({
          title: domain,
          description: 'Click to read the full article',
        });
      } catch (error) {
        console.error('Error fetching article metadata:', error);
      }
    };

    fetchMetadata();
  }, [url]);

  if (!metadata) {
    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-500 hover:text-blue-600 flex items-center gap-2"
      >
        <ExternalLink size={16} />
        {url}
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block my-2 rounded-lg border border-gray-200 overflow-hidden bg-white hover:bg-gray-50 transition-colors"
    >
      <div className="p-4 flex items-start gap-4">
        {metadata.image && (
          <div className="flex-shrink-0 w-24 h-24">
            <img
              src={metadata.image}
              alt={metadata.title || 'Article thumbnail'}
              className="w-full h-full object-cover rounded"
            />
          </div>
        )}
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <span>{metadata.siteName || new URL(url).hostname.replace('www.', '')}</span>
            <ExternalLink size={14} />
          </div>
          <h3 className="font-medium text-gray-900 mb-1 truncate">
            {metadata.title}
          </h3>
          {metadata.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {metadata.description}
            </p>
          )}
        </div>
      </div>
    </a>
  );
};

export default ArticlePreview;
